import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ReleaseDate } from "@/components/ReleaseDate";
import { PatchNotesButton } from "@/components/PatchNotesButton";
import { useAppVersion } from "@/hooks/useAppVersion";
import { CommunityPolls } from "@/components/CommunityPolls";
import { PollCreateDialog } from "@/pages/admin/PollCreateDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Map, Sparkles, MessageSquare, ArrowUp, ChevronDown, ChevronUp, Shield, Pin,
  Plus, Trash2, Send, Check, X, Lock, Rocket, Lightbulb, Users2, PackageCheck,
} from "lucide-react";

const STATUS_META = {
  ready: { label: "Release updates · publication unconfirmed", color: "#67e8f9", bg: "bg-cyan-400/10 border-cyan-300/40 text-cyan-200", icon: PackageCheck, order: 3 },
  in_progress: { label: "In Progress", color: "#38bdf8", bg: "bg-cyan-400/10 border-cyan-300/40 text-cyan-200", icon: Rocket, order: 0 },
  planned:     { label: "Planned",     color: "#fbbf24", bg: "bg-amber-400/10 border-amber-300/40 text-amber-200", icon: Lightbulb, order: 1 },
  proposed:    { label: "Community",   color: "#a78bfa", bg: "bg-violet-400/10 border-violet-300/40 text-violet-200", icon: Users2, order: 2 },
  shipped:     { label: "Shipped",     color: "#34d399", bg: "bg-emerald-400/10 border-emerald-300/40 text-emerald-200", icon: PackageCheck, order: 3 },
  declined:    { label: "Declined",    color: "#94a3b8", bg: "bg-white/5 border-white/15 text-white/60",              icon: X,          order: 4 },
};
const STATUS_ORDER = ["in_progress", "ready", "planned", "proposed", "shipped", "declined"];

/**
 * /roadmap — the public transparency page.
 *
 * Renders four grouped columns (In Progress → Planned → Community Proposals →
 * Shipped) so players can see everything Playden is building, thinking about,
 * or has already delivered. Any logged-in player can propose a new item, 👍
 * upvote anything, and reply on the thread. Admin comments carry a badge.
 */
export default function RoadmapPage() {
  const { user } = useAuth();
  const version = useAppVersion();
  const [newOpen, setNewOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [showShipped, setShowShipped] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["roadmap", "board", version],
    queryFn: async () => (await api.get("/roadmap")).data,
    staleTime: 15_000,
  });
  const items = useMemo(() => data?.items || [], [data?.items]);

  const grouped = useMemo(() => {
    const g = { in_progress: [], ready: [], planned: [], proposed: [], shipped: [], declined: [] };
    for (const it of items) {
      const s = it.status in g ? it.status : "planned";
      g[s].push(it);
    }
    // Sort each group: pinned first, then upvotes desc, then updated desc.
    for (const s of Object.keys(g)) {
      g[s].sort((a, b) =>
        (b.pinned - a.pinned) ||
        ((s === "ready" || s === "shipped") ? String(b.version || "").localeCompare(String(a.version || ""), undefined, { numeric: true }) : 0) ||
        (b.upvotes_count - a.upvotes_count) ||
        String(b.updated_at || "").localeCompare(String(a.updated_at || ""))
      );
    }
    return g;
  }, [items]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="roadmap-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Map className="h-4 w-4 text-cyan-300" />
            <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-cyan-300">Playden Roadmap</span>
          </div>
          <h1 className="font-display text-3xl sm:text-5xl text-white tracking-[0.05em] leading-[1.05]">
            What we&apos;re building
          </h1>
          <p className="mt-2 text-white/60 text-sm max-w-2xl">
            Every update, every plan, every player idea — out in the open. Log in to propose changes, 👍 upvote what you want to see next, or reply to any thread. Admin replies carry a <span className="inline-flex items-center gap-1 rounded-full border border-cyan-300/40 bg-cyan-400/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.24em] text-cyan-200"><Shield className="h-2.5 w-2.5" /> Admin</span> badge.
          </p>
          <p className="mt-2 text-white/45 text-xs">
            Heads up: every update ships new code on top of the existing database — nothing you own is ever wiped by a redeploy.
          </p>
          <div className="mt-5"><PatchNotesButton testId="roadmap-patch-notes-button" /></div>
        </div>
        {user ? (
          <Button onClick={() => setNewOpen(true)} data-testid="roadmap-new-proposal"
            className="bg-cyan-400 text-black hover:bg-cyan-300 shrink-0">
            <Plus className="h-4 w-4 mr-1" /> Propose a change
          </Button>
        ) : (
          <Link to="/login">
            <Button className="bg-cyan-400 text-black hover:bg-cyan-300 shrink-0" data-testid="roadmap-signin-prompt">
              Sign in to propose
            </Button>
          </Link>
        )}
      </div>

      {isLoading ? (
        <div className="text-white/50 text-sm">Loading roadmap…</div>
      ) : (
        <div className="space-y-6">
          <CommunityPolls onOpen={setDetailId} />
          {STATUS_ORDER.filter((s) => s !== "shipped" && s !== "declined").map((s) => (
            <StatusSection key={s} status={s} items={grouped[s]} onOpen={setDetailId} />
          ))}
          <button onClick={() => setShowShipped((v) => !v)} data-testid="roadmap-toggle-shipped"
            className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-white/50 hover:text-white transition-colors">
            {showShipped ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            {showShipped ? "Hide" : "Show"} Shipped · {grouped.shipped.length}
          </button>
          {showShipped && <StatusSection status="shipped" items={grouped.shipped} onOpen={setDetailId} />}
          {grouped.declined.length > 0 && (
            <StatusSection status="declined" items={grouped.declined} onOpen={setDetailId} />
          )}
        </div>
      )}

      <NewProposalDialog open={newOpen} onOpenChange={setNewOpen} />
      <ItemDetailDialog itemId={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}

function StatusSection({ status, items, onOpen }) {
  const meta = STATUS_META[status];
  const Icon = meta.icon;
  return (
    <div data-testid={`roadmap-section-${status}`}>
      <div className="flex items-baseline gap-2 mb-3">
        <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.28em] ${meta.bg}`}>
          <Icon className="h-3 w-3" /> {meta.label}
        </span>
        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/10 bg-black/20 p-5 text-center text-white/40 text-[12px]">
          Nothing here yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((it) => <ItemCard key={it.id} item={it} onOpen={() => onOpen(it.id)} />)}
        </div>
      )}
    </div>
  );
}

function ItemCard({ item, onOpen }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const meta = STATUS_META[item.status] || STATUS_META.planned;

  const upvote = useMutation({
    mutationFn: async () => (await api.post(`/roadmap/${item.id}/upvote`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["roadmap"] }),
    onError: (e) => toast.error(apiError(e, "Upvote failed")),
  });

  return (
    <div className="rounded-2xl border border-white/10 bg-black/30 hover:border-white/20 transition-colors p-3 flex flex-col gap-2" data-testid={`roadmap-item-${item.id}`}>
      <div className="flex items-start gap-2">
        <button
          onClick={() => { if (!user) return toast("Sign in to upvote", { icon: "🔒" }); upvote.mutate(); }}
          disabled={upvote.isPending || item.closed}
          data-testid={`roadmap-item-${item.id}-upvote`}
          className={`shrink-0 flex flex-col items-center rounded-lg border px-2 py-1.5 transition-colors ${
            item.you_upvoted
              ? "border-cyan-300/60 bg-cyan-400/15 text-cyan-200"
              : "border-white/15 bg-white/[0.03] text-white/60 hover:border-white/30 hover:text-white"
          }`}
          title={item.you_upvoted ? "Remove upvote" : "Upvote"}
        >
          <ArrowUp className="h-4 w-4" />
          <span className="font-mono text-[11px] tabular-nums">{item.upvotes_count}</span>
        </button>
        <button onClick={onOpen} className="flex-1 text-left min-w-0" data-testid={`roadmap-item-${item.id}-open`}>
          <div className="flex items-center gap-1.5 flex-wrap mb-1">
            {item.pinned && <Pin className="h-3 w-3 text-amber-300" />}
            {item.closed && <Lock className="h-3 w-3 text-white/40" />}
            {item.category && (
              <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/45">{item.category}</span>
            )}
            {item.version && (
              <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-emerald-300/70">{item.version}</span>
            )}
          </div>
          <div className="text-white text-[14px] font-display leading-snug">{item.title}</div>
          <div className="mt-1 text-[12px] text-white/60 line-clamp-2">{item.description}</div>
          <div className="mt-2 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
            <span className="inline-flex items-center gap-1">
              <MessageSquare className="h-3 w-3" /> {item.comments_count}
            </span>
            {item.source === "community" && (
              <span className="text-violet-300/70">by {item.author_name || "player"}</span>
            )}
            <span className={`ml-auto inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 ${meta.bg}`}>
              {meta.label}
            </span>
          </div>
        </button>
      </div>
      {item.source === "changelog" && <ReleaseDate entry={item} testId={`roadmap-date-${item.id}`} />}
    </div>
  );
}

function NewProposalDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");

  const create = useMutation({
    mutationFn: async () => (await api.post("/roadmap", {
      title: title.trim(), description: description.trim(), category: category.trim() || null,
    })).data,
    onSuccess: () => {
      toast.success("Proposal submitted — thanks for the idea!");
      qc.invalidateQueries({ queryKey: ["roadmap"] });
      onOpenChange(false);
      setTitle(""); setDescription(""); setCategory("");
    },
    onError: (e) => toast.error(apiError(e, "Couldn't submit")),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-[hsl(222_22%_7%)] border-white/10" data-testid="roadmap-new-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-cyan-300" /> Propose a change
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs">
            Anything you&apos;d love to see in Playden — new features, tweaks, or fixes. Every proposal is public, upvotable, and gets a reply from the admin.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={140}
              placeholder="What's the idea in one line?"
              data-testid="roadmap-new-title" className="mt-1 bg-white/5 border-white/10 text-white" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Description</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={4000}
              placeholder="What should it do, and why?"
              data-testid="roadmap-new-desc" className="mt-1 bg-white/5 border-white/10 text-white min-h-[110px]" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Category (optional)</label>
            <Input value={category} onChange={(e) => setCategory(e.target.value)} maxLength={48}
              placeholder="e.g. Arena · Cavern · Store · 3D Prints"
              data-testid="roadmap-new-category" className="mt-1 bg-white/5 border-white/10 text-white" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-white/70">Cancel</Button>
          <Button onClick={() => create.mutate()}
            disabled={create.isPending || title.trim().length < 3 || description.trim().length < 3}
            data-testid="roadmap-new-submit"
            className="bg-cyan-400 text-black hover:bg-cyan-300">
            {create.isPending ? "Submitting…" : "Submit"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ItemDetailDialog({ itemId, onClose }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [replyText, setReplyText] = useState("");

  const { data: item, isLoading } = useQuery({
    queryKey: ["roadmap", itemId],
    queryFn: async () => (await api.get(`/roadmap/${itemId}`)).data,
    enabled: !!itemId,
    staleTime: 5_000,
  });

  const comment = useMutation({
    mutationFn: async () => (await api.post(`/roadmap/${itemId}/comments`, { body: replyText.trim() })).data,
    onSuccess: () => {
      setReplyText("");
      qc.invalidateQueries({ queryKey: ["roadmap", itemId] });
      qc.invalidateQueries({ queryKey: ["roadmap"] });
    },
    onError: (e) => toast.error(apiError(e, "Reply failed")),
  });

  const setStatus = useMutation({
    mutationFn: async (status) => (await api.patch(`/roadmap/${itemId}`, { status })).data,
    onSuccess: (_, status) => {
      toast.success(`Marked ${STATUS_META[status]?.label || status}`);
      qc.invalidateQueries({ queryKey: ["roadmap", itemId] });
      qc.invalidateQueries({ queryKey: ["roadmap"] });
    },
    onError: (e) => toast.error(apiError(e, "Update failed")),
  });

  const togglePin = useMutation({
    mutationFn: async () => (await api.patch(`/roadmap/${itemId}`, { pinned: !item.pinned })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["roadmap", itemId] });
      qc.invalidateQueries({ queryKey: ["roadmap"] });
    },
    onError: (e) => toast.error(apiError(e, "Pin failed")),
  });

  const toggleClose = useMutation({
    mutationFn: async () => (await api.patch(`/roadmap/${itemId}`, { closed: !item.closed })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["roadmap", itemId] });
      qc.invalidateQueries({ queryKey: ["roadmap"] });
    },
    onError: (e) => toast.error(apiError(e, "Close failed")),
  });

  const del = useMutation({
    mutationFn: async () => (await api.delete(`/roadmap/${itemId}`)).data,
    onSuccess: () => {
      toast.success("Deleted");
      qc.invalidateQueries({ queryKey: ["roadmap"] });
      onClose();
    },
    onError: (e) => toast.error(apiError(e, "Delete failed")),
  });

  const meta = item ? (STATUS_META[item.status] || STATUS_META.planned) : STATUS_META.planned;

  return (
    <Dialog open={!!itemId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl bg-[hsl(222_22%_7%)] border-white/10 max-h-[85vh] overflow-y-auto" data-testid="roadmap-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2 flex-wrap">
            {item?.pinned && <Pin className="h-4 w-4 text-amber-300" />}
            {isLoading || !item ? "Loading…" : item.title}
            {item && (
              <span className={`ml-auto inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.22em] ${meta.bg}`}>
                {meta.label}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-white/55 text-xs flex items-center gap-2 flex-wrap">
            {item?.category && <span className="font-mono uppercase tracking-[0.22em] text-white/60">{item.category}</span>}
            {item?.source === "community" && <span>· proposed by <strong className="text-violet-300">{item?.author_name || "player"}</strong></span>}
            {item?.version && <span className="font-mono uppercase tracking-[0.22em] text-emerald-300/70">· v{item.version}</span>}
            {!item && <span>Discussion thread</span>}
          </DialogDescription>
        </DialogHeader>

        {isLoading || !item ? (
          <div className="text-white/50 py-6 text-center">Loading…</div>
        ) : (
          <>
            <div className="text-white/80 text-[13px] leading-relaxed whitespace-pre-wrap">{item.description}</div>

            {item.admin_note && (
              <div className="rounded-lg border border-cyan-300/30 bg-cyan-400/[0.06] p-3">
                <div className="flex items-center gap-1 mb-1 font-mono text-[10px] uppercase tracking-[0.24em] text-cyan-300">
                  <Shield className="h-3 w-3" /> Admin note
                </div>
                <div className="text-white/85 text-[13px] whitespace-pre-wrap">{item.admin_note}</div>
              </div>
            )}

            {/* Admin action row */}
            {isAdmin && (
              <div className="rounded-lg border border-cyan-300/25 bg-cyan-400/[0.04] p-2 flex flex-wrap gap-1.5" data-testid="roadmap-admin-controls">
                <PollCreateDialog item={item} />
                {STATUS_ORDER.map((s) => (
                  <Button key={s} size="sm" variant="outline"
                    onClick={() => setStatus.mutate(s)}
                    disabled={setStatus.isPending || item.status === s}
                    data-testid={`roadmap-admin-set-${s}`}
                    className={`h-7 px-2 text-[10px] border-white/20 ${item.status === s ? "text-white bg-white/10" : "text-white/70 hover:bg-white/5"}`}>
                    → {STATUS_META[s].label}
                  </Button>
                ))}
                <Button size="sm" variant="outline" onClick={() => togglePin.mutate()} disabled={togglePin.isPending}
                  data-testid="roadmap-admin-pin"
                  className="h-7 px-2 text-[10px] border-amber-300/40 text-amber-200 hover:bg-amber-400/10">
                  <Pin className="h-3 w-3 mr-1" /> {item.pinned ? "Unpin" : "Pin"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => toggleClose.mutate()} disabled={toggleClose.isPending}
                  data-testid="roadmap-admin-close"
                  className="h-7 px-2 text-[10px] border-white/15 text-white/70 hover:bg-white/5">
                  <Lock className="h-3 w-3 mr-1" /> {item.closed ? "Reopen" : "Close"}
                </Button>
                <Button size="sm" variant="outline"
                  onClick={() => { if (window.confirm(`Delete "${item.title}"? This cannot be undone.`)) del.mutate(); }}
                  disabled={del.isPending}
                  data-testid="roadmap-admin-delete"
                  className="ml-auto h-7 px-2 text-[10px] border-red-400/30 text-red-300 hover:bg-red-400/10">
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            )}

            {/* Thread */}
            <div className="space-y-2">
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/50">
                Discussion · {item.comments.length}
              </div>
              {item.comments.length === 0 && (
                <div className="text-white/40 text-[12px]">No replies yet. Kick off the discussion below.</div>
              )}
              {item.comments.map((c) => (
                <CommentRow key={c.id} c={c} itemId={item.id} onDeleted={() => qc.invalidateQueries({ queryKey: ["roadmap", item.id] })} />
              ))}
            </div>

            {/* Reply box */}
            {user ? (
              item.closed ? (
                <div className="text-white/40 text-[12px] flex items-center gap-1">
                  <Lock className="h-3 w-3" /> This item is closed to new replies.
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Reply to this proposal…" maxLength={2000}
                    data-testid="roadmap-reply-input"
                    className="bg-white/5 border-white/10 text-white text-sm min-h-[60px] flex-1" />
                  <Button onClick={() => comment.mutate()}
                    disabled={comment.isPending || !replyText.trim()}
                    data-testid="roadmap-reply-submit"
                    className="bg-cyan-400 text-black hover:bg-cyan-300 shrink-0">
                    <Send className="h-4 w-4 mr-1" /> Reply
                  </Button>
                </div>
              )
            ) : (
              <div className="text-white/50 text-[12px]">
                <Link to="/login" className="text-cyan-300 hover:text-cyan-200 underline underline-offset-4">Sign in</Link> to reply on this thread.
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CommentRow({ c, itemId, onDeleted }) {
  const { user } = useAuth();
  const canDelete = user && !c.deleted && (user.role === "admin" || user.id === c.user_id);

  const del = useMutation({
    mutationFn: async () => (await api.delete(`/roadmap/${itemId}/comments/${c.id}`)).data,
    onSuccess: () => { toast("Deleted"); onDeleted?.(); },
    onError: (e) => toast.error(apiError(e, "Delete failed")),
  });

  return (
    <div className={`rounded-lg border px-3 py-2 ${c.is_admin ? "border-cyan-300/30 bg-cyan-400/[0.05]" : "border-white/10 bg-white/[0.02]"}`}
      data-testid={`roadmap-comment-${c.id}`}>
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <span className="text-white/85 text-[12px] font-semibold">{c.author_name || "Player"}</span>
        {c.is_admin && (
          <span className="inline-flex items-center gap-1 rounded-full border border-cyan-300/40 bg-cyan-400/10 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.24em] text-cyan-200">
            <Shield className="h-2.5 w-2.5" /> Admin
          </span>
        )}
        <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/35 ml-auto">
          {c.created_at?.slice(0, 16)?.replace("T", " ")}
        </span>
        {canDelete && (
          <button onClick={() => del.mutate()} disabled={del.isPending}
            data-testid={`roadmap-comment-${c.id}-delete`}
            className="text-white/40 hover:text-red-300 transition-colors">
            <Trash2 className="h-3 w-3" />
          </button>
        )}
      </div>
      <div className={`text-[13px] whitespace-pre-wrap ${c.deleted ? "text-white/30 italic" : "text-white/80"}`}>{c.body}</div>
    </div>
  );
}
